<template>
  <base-modal
    ref="modalRef"
    :nested="false"
    size="xl"
  >
    <template #header>
      <div class="assets-grid-title">
        <div class="assets-grid-title__icon">
          <i class="bi bi-hdd-rack-fill" />
        </div>
        <div class="assets-grid-title__text">
          <span v-if="isReadOnly">{{ $t('components.assetGroupsWithLocation.modal.viewTitle') }}: {{ groupName }}</span>
          <span v-else>{{ groupName ? `${$t('components.assetGroupsWithLocation.modal.editTitle')}: ${groupName}` : $t('components.assetGroupsWithLocation.modal.viewTitle') }}</span>
        </div>
      </div>
    </template>

    <template #body>
      <div class="asset-group-location-form">
        <!-- Alert for errors -->
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-center gap-2 mb-4"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ errorMessage }}</span>
        </div>

        <form @submit.prevent="handleSubmit">
          <!-- Location Images & Schemes Section -->
          <div class="form-section">
            <h5 class="form-section-title">
              <i class="bi bi-image me-2" />
              {{ $t('components.assetGroupsWithLocation.modal.locationImage') }}
            </h5>

            <div class="alert alert-info d-flex align-items-start gap-2 mb-3">
              <i class="bi bi-info-circle-fill mt-1" />
              <small>{{ $t('components.assetGroupsWithLocation.modal.hints.locationImage') }}</small>
            </div>

            <div class="scheme-upload-section">
              <div class="position-sticky top-0 z-3 bg-white py-2 border-bottom mb-3">
                <div class="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div class="text-muted small">
                    {{ $t('components.assetGroupsWithLocation.modal.hints.locationImage') }}
                  </div>
                  <button
                    v-if="!isReadOnly"
                    type="button"
                    class="btn assets-toolbar-btn"
                    @click="addLocationImageEntry"
                  >
                    <i class="bi bi-plus-circle me-1" />
                    Dodaj zdjęcie
                  </button>
                </div>
              </div>

              <div
                v-for="(image, imgIdx) in locationImages"
                :key="image.uid"
                class="card mb-3"
                :class="{ 'border-danger': image.remove }"
              >
                <div class="card-body">
                  <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                    <div class="fw-bold">
                      {{ $t('components.assetGroupsWithLocation.modal.locationImage') }} #{{ imgIdx + 1 }}
                      <span
                        v-if="image.existing"
                        class="text-muted"
                      >({{ $t('common.existing') }})</span>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                      <button
                        v-if="!isReadOnly"
                        type="button"
                        class="btn assets-toolbar-btn btn-sm"
                        :disabled="image.remove"
                        @click="addSchemeEntry(imgIdx)"
                      >
                        <i class="bi bi-plus-circle me-1" />
                        {{ $t('components.assetGroupsWithLocation.modal.addScheme') }}
                      </button>
                      <button
                        v-if="!isReadOnly"
                        type="button"
                        class="btn btn-sm"
                        :class="image.remove ? 'btn-outline-secondary' : 'btn-outline-danger'"
                        @click="toggleRemoveLocationImage(imgIdx)"
                      >
                        <i
                          :class="image.remove ? 'bi bi-arrow-counterclockwise' : 'bi bi-trash'"
                          class="me-1"
                        />
                        {{ image.remove ? $t('common.restore') : $t('common.remove') }}
                      </button>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">
                      <i class="bi bi-file-image me-2" />
                      {{ $t('components.assetGroupsWithLocation.modal.fields.locationImage') }}
                    </label>
                    <div v-if="!isReadOnly">
                      <input
                        :id="`locationImage-${image.uid}`"
                        type="file"
                        class="form-control"
                        :class="{ 'is-invalid': image.error }"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        :disabled="image.remove"
                        @change="handleLocationImageChange($event, imgIdx)"
                      >
                      <div
                        v-if="image.error"
                        class="invalid-feedback d-block"
                      >
                        {{ image.error }}
                      </div>
                    </div>

                    <div
                      v-if="image.preview"
                      class="image-preview mt-2"
                    >
                      <img
                        :src="image.preview"
                        alt="Location preview"
                      >
                      <button
                        v-if="!isReadOnly && image.file"
                        type="button"
                        class="btn-remove-preview"
                        :disabled="image.remove"
                        @click="clearLocationImageFile(imgIdx)"
                      >
                        <i class="bi bi-x-circle-fill" />
                      </button>
                    </div>
                    <div
                      v-if="isReadOnly && !image.preview"
                      class="text-muted"
                    >
                      {{ $t('components.assetGroupsWithLocation.modal.noImage') }}
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">
                      Nazwa zdjęcia
                    </label>
                    <input
                      v-model="image.name"
                      type="text"
                      class="form-control"
                      maxlength="255"
                      :disabled="isReadOnly || image.remove"
                    >
                  </div>

                  <div class="form-section">
                    <h6 class="form-section-title">
                      <i class="bi bi-diagram-3 me-2" />
                      {{ $t('components.assetGroupsWithLocation.modal.schemes') }}
                    </h6>

                    <div
                      v-for="(scheme, idx) in image.schemes"
                      :key="scheme.uid"
                      class="card mb-3"
                      :class="{ 'border-danger': scheme.remove }"
                    >
                      <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <div class="fw-bold">
                            {{ $t('components.assetGroupsWithLocation.modal.fields.scheme') }} #{{ idx + 1 }}
                            <span
                              v-if="scheme.existing"
                              class="text-muted"
                            >({{ $t('common.existing') }})</span>
                          </div>
                          <button
                            v-if="!isReadOnly"
                            type="button"
                            class="btn btn-sm"
                            :class="scheme.remove ? 'btn-outline-secondary' : 'btn-outline-danger'"
                            @click="toggleRemoveScheme(imgIdx, idx)"
                          >
                            <i
                              :class="scheme.remove ? 'bi bi-arrow-counterclockwise' : 'bi bi-trash'"
                              class="me-1"
                            />
                            {{ scheme.remove ? $t('common.restore') : $t('common.remove') }}
                          </button>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">
                            <i class="bi bi-file-image me-2" />
                            {{ $t('components.assetGroupsWithLocation.modal.fields.schemeFile') }}
                          </label>
                          <div v-if="!isReadOnly">
                            <input
                              :id="`schemeFile-${scheme.uid}`"
                              type="file"
                              class="form-control"
                              :class="{ 'is-invalid': scheme.error }"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              :disabled="scheme.remove"
                              @change="handleSchemeFileChange($event, imgIdx, idx)"
                            >
                            <small class="text-muted">
                              {{ $t('components.assetGroupsWithLocation.modal.hints.schemeFile') }}
                            </small>
                            <div
                              v-if="scheme.error"
                              class="invalid-feedback d-block"
                            >
                              {{ scheme.error }}
                            </div>
                          </div>

                          <div
                            v-if="scheme.preview"
                            class="image-preview mt-2"
                          >
                            <img
                              :src="scheme.preview"
                              alt="Scheme preview"
                            >
                            <button
                              v-if="!isReadOnly && scheme.file"
                              type="button"
                              class="btn-remove-preview"
                              :disabled="scheme.remove"
                              @click="clearSchemeFile(imgIdx, idx)"
                            >
                              <i class="bi bi-x-circle-fill" />
                            </button>
                          </div>
                          <div
                            v-if="isReadOnly && !scheme.preview"
                            class="text-muted"
                          >
                            {{ $t('components.assetGroupsWithLocation.modal.noSchemeImage') }}
                          </div>
                        </div>

                        <div class="mb-3">
                          <label
                            class="form-label"
                            :class="{ required: scheme.file }"
                          >
                            {{ $t('components.assetGroupsWithLocation.modal.fields.schemeName') }}
                          </label>
                          <input
                            v-model="scheme.name"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': scheme.error && !scheme.name }"
                            :placeholder="$t('components.assetGroupsWithLocation.modal.placeholders.schemeName')"
                            maxlength="255"
                            :disabled="isReadOnly || scheme.remove"
                          >
                        </div>

                        <div class="mb-3">
                          <label class="form-label">
                            {{ $t('components.assetGroupsWithLocation.modal.fields.schemeDescription') }}
                          </label>
                          <textarea
                            v-model="scheme.description"
                            class="form-control"
                            rows="2"
                            :placeholder="$t('components.assetGroupsWithLocation.modal.placeholders.schemeDescription')"
                            :disabled="isReadOnly || scheme.remove"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="text-muted small">
                      {{ $t('components.assetGroupsWithLocation.modal.hints.schemeFile') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
        >
          {{ $t('components.assetGroupsWithLocation.modal.close') }}
        </button>
        <button
          v-if="!isReadOnly"
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="handleSubmit"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm me-2"
          />
          {{ $t('components.assetGroupsWithLocation.modal.save') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/components/content/_modals/core/BaseModal.vue'
import { addAssetGroupLocation, addAssetGroupScheme, deleteAssetGroupScheme, deleteAssetGroupLocationImage } from '@/crud/asset-group'

type SchemeEntry = {
  uid: string
  id: number | null
  name: string
  description: string
  file: File | null
  preview: string | null
  remove: boolean
  existing: boolean
  error: string | null
}

type LocationImageEntry = {
  uid: string
  id: number | null
  name: string
  file: File | null
  preview: string | null
  remove: boolean
  existing: boolean
  error: string | null
  schemes: SchemeEntry[]
}

export default defineComponent({
  name: 'AssetGroupWithLocationFormModal',
  components: { BaseModal },
  emits: ['saved'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const vm = getCurrentInstance()
    const toast = vm?.proxy?.$toast
    const modalRef = ref<InstanceType<typeof BaseModal> | null>(null)

    const groupId = ref<number | null>(null)
    const groupName = ref<string>('')
    const isReadOnly = ref(false)
    const loading = ref(false)
    const errorMessage = ref<string | null>(null)

    const locationImages = ref<LocationImageEntry[]>([])

    const validateImageFile = (file: File): string | null => {
      const maxSize = 5 * 1024 * 1024 // 5MB
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

      if (!allowedTypes.includes(file.type)) {
        return t('components.assetGroupsWithLocation.modal.validation.invalidImageType')
      }

      if (file.size > maxSize) {
        return t('components.assetGroupsWithLocation.modal.validation.imageTooLarge')
      }

      return null
    }

    const convertPathToUrl = (filePath: string): string => {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    }

    const createSchemeEntry = (data?: Partial<SchemeEntry>): SchemeEntry => ({
      uid: crypto.randomUUID(),
      id: data?.id ?? null,
      name: data?.name ?? '',
      description: data?.description ?? '',
      file: data?.file ?? null,
      preview: data?.preview ?? null,
      remove: data?.remove ?? false,
      existing: data?.existing ?? false,
      error: null
    })

    const createLocationImageEntry = (data?: Partial<LocationImageEntry>): LocationImageEntry => ({
      uid: crypto.randomUUID(),
      id: data?.id ?? null,
      name: data?.name ?? '',
      file: data?.file ?? null,
      preview: data?.preview ?? null,
      remove: data?.remove ?? false,
      existing: data?.existing ?? false,
      error: null,
      schemes: data?.schemes ?? []
    })

    const addLocationImageEntry = () => {
      locationImages.value.push(createLocationImageEntry())
    }

    const handleLocationImageChange = (event: Event, imgIdx: number) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      const entry = locationImages.value[imgIdx]
      if (!entry) return

      entry.error = null
      entry.remove = false

      if (!file) {
        entry.file = null
        entry.preview = entry.existing ? entry.preview : null
        return
      }

      const error = validateImageFile(file)
      if (error) {
        entry.error = error
        entry.file = null
        entry.preview = entry.existing ? entry.preview : null
        return
      }

      entry.file = file

      const reader = new FileReader()
      reader.onload = (e) => {
        entry.preview = e.target?.result as string
      }
      reader.readAsDataURL(file)
    }

    const clearLocationImageFile = (imgIdx: number) => {
      const entry = locationImages.value[imgIdx]
      if (!entry) return
      entry.file = null
      if (!entry.existing) {
        entry.preview = null
      }
    }

    const toggleRemoveLocationImage = (imgIdx: number) => {
      const entry = locationImages.value[imgIdx]
      if (!entry) return
      entry.remove = !entry.remove
      entry.error = null
    }

    const addSchemeEntry = (imgIdx: number) => {
      const image = locationImages.value[imgIdx]
      if (!image) return
      image.schemes.push(createSchemeEntry())
    }

    const getSchemeList = (imgIdx: number): SchemeEntry[] | null => {
      return locationImages.value[imgIdx]?.schemes ?? null
    }

    const handleSchemeFileChange = (event: Event, imgIdx: number, idx: number) => {
      const list = getSchemeList(imgIdx)
      if (!list) return
      const entry = list[idx]
      if (!entry) return

      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      entry.error = null
      entry.remove = false

      if (!file) {
        entry.file = null
        entry.preview = entry.existing ? entry.preview : null
        return
      }

      const error = validateImageFile(file)
      if (error) {
        entry.error = error
        entry.file = null
        entry.preview = entry.existing ? entry.preview : null
        return
      }

      entry.file = file

      const reader = new FileReader()
      reader.onload = (e) => {
        entry.preview = e.target?.result as string
      }
      reader.readAsDataURL(file)
    }

    const clearSchemeFile = (imgIdx: number, idx: number) => {
      const list = getSchemeList(imgIdx)
      if (!list) return
      const entry = list[idx]
      if (!entry) return
      entry.file = null
      if (!entry.existing) {
        entry.preview = null
      }
    }

    const toggleRemoveScheme = (imgIdx: number, idx: number) => {
      const list = getSchemeList(imgIdx)
      if (!list) return
      const entry = list[idx]
      if (!entry) return
      entry.remove = !entry.remove
      entry.error = null
    }

    const resetForm = () => {
      groupId.value = null
      groupName.value = ''
      errorMessage.value = null

      locationImages.value = []
    }

    const openModal = async (groupData?: any, readOnly = false) => {
      resetForm()
      isReadOnly.value = readOnly
      modalRef.value?.openModal()

      if (groupData) {
        groupId.value = groupData.id
        groupName.value = groupData.name || `Group #${groupData.id}`

        const nextImages: LocationImageEntry[] = []
        const images = groupData.location?.images || []
        images.forEach((img: any) => {
          const schemes = (img.schemes || []).map((s: any) => {
            const schemeFilePath = s.filePath || s.file_path
            return createSchemeEntry({
              id: s.id || null,
              name: s.name || '',
              description: s.description || '',
              preview: schemeFilePath ? convertPathToUrl(schemeFilePath) : null,
              existing: true
            })
          })
          const imageFilePath = img.filePath || img.file_path
          nextImages.push(
            createLocationImageEntry({
              id: img.id || null,
              name: img.name || '',
              preview: imageFilePath ? convertPathToUrl(imageFilePath) : null,
              existing: true,
              schemes
            })
          )
        })

        const schemesWithoutImage = groupData.location?.schemesWithoutImage || []
        if (!images.length && schemesWithoutImage.length) {
          const schemes = schemesWithoutImage.map((s: any) => {
            const schemeFilePath = s.filePath || s.file_path
            return createSchemeEntry({
              id: s.id || null,
              name: s.name || '',
              description: s.description || '',
              preview: schemeFilePath ? convertPathToUrl(schemeFilePath) : null,
              existing: true
            })
          })
          nextImages.push(
            createLocationImageEntry({
              schemes
            })
          )
        }

        locationImages.value = nextImages
      }
    }

    const closeModal = () => {
      modalRef.value?.closeModal()
      resetForm()
    }

    const handleSubmit = async () => {
      let hasSchemeError = false
      errorMessage.value = null

      locationImages.value.forEach((image) => {
        image.schemes.forEach((entry) => {
          entry.error = null
          if (entry.remove) {
            return
          }
          if (entry.file && (!entry.name || entry.name.trim() === '')) {
            entry.error = t('components.assetGroupsWithLocation.modal.validation.schemeNameRequired')
            hasSchemeError = true
          }
        })
      })


      if (hasSchemeError) {
        return
      }

      loading.value = true

      try {
        if (!groupId.value) {
          return
        }

        for (const image of locationImages.value) {
          if (image.remove && image.id) {
            await deleteAssetGroupLocationImage(groupId.value, image.id)
          }
        }

        const existingImageIds = new Set(
          locationImages.value
            .filter(image => image.id)
            .map(image => image.id as number)
        )

        for (const image of locationImages.value) {
          if (image.file) {
            const locationFormData = new FormData()
            locationFormData.append('location[image]', image.file)
            if (image.name) {
              locationFormData.append('location[imageName]', image.name)
            }
            const response = await addAssetGroupLocation(groupId.value, locationFormData)
            const images = response?.data?.group?.location?.images || []
            const newlyAdded = images.find((img: any) => img?.id && !existingImageIds.has(img.id))
            if (newlyAdded) {
              image.id = newlyAdded.id
              image.existing = true
              if (!image.preview) {
                image.preview = convertPathToUrl(newlyAdded.filePath || newlyAdded.file_path || '')
              }
              existingImageIds.add(newlyAdded.id)
            }
          }
        }

        for (const image of locationImages.value) {
          for (const entry of image.schemes) {
            if (entry.remove && entry.id) {
              await deleteAssetGroupScheme(groupId.value, entry.id)
              continue
            }

            if (image.remove) {
              continue
            }

            if (entry.file && entry.name) {
              const schemeFormData = new FormData()
              schemeFormData.append('scheme_file', entry.file)
              schemeFormData.append('scheme_name', entry.name)
              if (entry.description) {
                schemeFormData.append('scheme_description', entry.description)
              }
              if (image.id) {
                schemeFormData.append('location_image_id', String(image.id))
              }
              await addAssetGroupScheme(groupId.value, schemeFormData)
            }
          }
        }


        toast?.success(t('components.assetGroupsWithLocation.modal.successMessage'))
        emit('saved')
        closeModal()
      } catch (e: any) {
        const error = e?.response?.data?.error || e?.message || t('components.assetGroupsWithLocation.modal.error')
        errorMessage.value = error
        toast?.error(error)
      } finally {
        loading.value = false
      }
    }

    return {
      modalRef,
      groupName,
      isReadOnly,
      loading,
      errorMessage,
      locationImages,
      handleLocationImageChange,
      clearLocationImageFile,
      toggleRemoveLocationImage,
      addLocationImageEntry,
      addSchemeEntry,
      handleSchemeFileChange,
      clearSchemeFile,
      toggleRemoveScheme,
      openModal,
      closeModal,
      handleSubmit
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/modal/asset-group-with-location-form-modal.scss';
</style>
